import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { NavigationContext } from '../../context/NavigationContext';
import { toast } from 'react-toastify';
import './TischkontenDetails.css';

interface RoundTable {
    id: number;
    name: string;
    number: number;
    iban: string;
    bic: string;
    kontoInhaber: string;
    bankName: string;
    billingName: string;
    billingEmail: string;
    billingPlz: string;
    billingCity: string;
    billingStreet: string;
    billingStreetNumber: string;
}

const TischkontenDetailsComponent: React.FC = () => {
    const authContext = useContext(AuthContext);
    const navigationContext = useContext(NavigationContext);
    const [roundTable, setRoundTable] = useState<RoundTable | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState<RoundTable | null>(null);

    if (!authContext) {
        throw new Error('TischkontenDetailsComponent must be used within an AuthProvider');
    }
    if (!navigationContext) {
        throw new Error('TischkontenDetailsComponent must be used within a NavigationProvider');
    }

    const { token } = authContext;
    const { path, navigateTo } = navigationContext;

    // Extract ID from path like /tischkonten/details/123
    const roundTableId = path.split('/').pop();

    useEffect(() => {
        const fetchRoundTable = async () => {
            if (!roundTableId || isNaN(Number(roundTableId))) {
                setError('Ungültige Tischkonto-ID');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`/api/roundtables/${roundTableId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error('Tischkonto nicht gefunden');
                    }
                    throw new Error('Fehler beim Laden des Tischkontos');
                }

                const data: RoundTable = await response.json();
                setRoundTable(data);
                setFormData(data);
                setError(null);
            } catch (error) {
                console.error('Error fetching round table:', error);
                setError(error instanceof Error ? error.message : 'Fehler beim Laden des Tischkontos');
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden des Tischkontos');
            } finally {
                setLoading(false);
            }
        };

        fetchRoundTable();
    }, [roundTableId, token]);

    const handleInputChange = (field: keyof RoundTable, value: string | number) => {
        if (!formData) return;
        
        setFormData({
            ...formData,
            [field]: value,
        });
    };

    const handleSave = async () => {
        if (!formData || !roundTableId) return;

        setSaving(true);
        try {
            const response = await fetch(`/api/roundtables/${roundTableId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                throw new Error('Fehler beim Speichern des Tischkontos');
            }

            const updatedData: RoundTable = await response.json();
            setRoundTable(updatedData);
            setFormData(updatedData);
            toast.success('Tischkonto erfolgreich aktualisiert');
        } catch (error) {
            console.error('Error updating round table:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Speichern des Tischkontos');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (formData && roundTable) {
            setFormData(roundTable);
        }
        navigateTo('/tischkonten');
    };

    const hasChanges = () => {
        if (!formData || !roundTable) return false;
        return JSON.stringify(formData) !== JSON.stringify(roundTable);
    };

    if (loading) {
        return <div className="tischkonten-details-loading">Lade Tischkonto...</div>;
    }

    if (error) {
        return (
            <div className="tischkonten-details-container">
                <div className="tischkonten-details-error">
                    <h2>Fehler</h2>
                    <p>{error}</p>
                    <button onClick={() => navigateTo('/tischkonten')}>Zurück zur Übersicht</button>
                </div>
            </div>
        );
    }

    if (!roundTable || !formData) {
        return (
            <div className="tischkonten-details-container">
                <div className="tischkonten-details-error">
                    <h2>Tischkonto nicht gefunden</h2>
                    <button onClick={() => navigateTo('/tischkonten')}>Zurück zur Übersicht</button>
                </div>
            </div>
        );
    }

    return (
        <div className="tischkonten-details-container">
            <div className="tischkonten-details-header">
                <h2>Tischkonto bearbeiten</h2>
                <div className="header-actions">
                    <button 
                        className="cancel-btn"
                        onClick={handleCancel}
                    >
                        Abbrechen
                    </button>
                    <button 
                        className="save-btn"
                        onClick={handleSave}
                        disabled={saving || !hasChanges()}
                    >
                        {saving ? 'Speichert...' : 'Speichern'}
                    </button>
                </div>
            </div>

            <div className="tischkonten-details-content">
                <div className="details-section">
                    <h3>Allgemeine Informationen</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Tischname *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder="z.B. Round Table Berlin"
                            />
                        </div>
                        <div className="form-group">
                            <label>Tischnummer *</label>
                            <input
                                type="number"
                                value={formData.number}
                                onChange={(e) => handleInputChange('number', parseInt(e.target.value) || 0)}
                                placeholder="z.B. 123"
                            />
                        </div>
                    </div>
                </div>

                <div className="details-section">
                    <h3>Kontoinformationen</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>IBAN</label>
                            <input
                                type="text"
                                value={formData.iban}
                                onChange={(e) => handleInputChange('iban', e.target.value)}
                                placeholder="DE89 3704 0044 0532 0130 00"
                            />
                        </div>
                        <div className="form-group">
                            <label>BIC</label>
                            <input
                                type="text"
                                value={formData.bic}
                                onChange={(e) => handleInputChange('bic', e.target.value)}
                                placeholder="COBADEFFXXX"
                            />
                        </div>
                        <div className="form-group">
                            <label>Kontoinhaber</label>
                            <input
                                type="text"
                                value={formData.kontoInhaber}
                                onChange={(e) => handleInputChange('kontoInhaber', e.target.value)}
                                placeholder="Round Table Berlin e.V."
                            />
                        </div>
                        <div className="form-group">
                            <label>Bank</label>
                            <input
                                type="text"
                                value={formData.bankName}
                                onChange={(e) => handleInputChange('bankName', e.target.value)}
                                placeholder="Commerzbank"
                            />
                        </div>
                    </div>
                </div>

                <div className="details-section">
                    <h3>Rechnungsadresse</h3>
                    <div className="form-grid">
                        <div className="form-group full-width">
                            <label>Name</label>
                            <input
                                type="text"
                                value={formData.billingName}
                                onChange={(e) => handleInputChange('billingName', e.target.value)}
                                placeholder="Round Table Berlin"
                            />
                        </div>
                        <div className="form-group full-width">
                            <label>E-Mail</label>
                            <input
                                type="email"
                                value={formData.billingEmail}
                                onChange={(e) => handleInputChange('billingEmail', e.target.value)}
                                placeholder="billing@rt-berlin.de"
                            />
                        </div>
                        <div className="form-group">
                            <label>Straße</label>
                            <input
                                type="text"
                                value={formData.billingStreet}
                                onChange={(e) => handleInputChange('billingStreet', e.target.value)}
                                placeholder="Unter den Linden"
                            />
                        </div>
                        <div className="form-group">
                            <label>Hausnummer</label>
                            <input
                                type="text"
                                value={formData.billingStreetNumber}
                                onChange={(e) => handleInputChange('billingStreetNumber', e.target.value)}
                                placeholder="1"
                            />
                        </div>
                        <div className="form-group">
                            <label>PLZ</label>
                            <input
                                type="text"
                                value={formData.billingPlz}
                                onChange={(e) => handleInputChange('billingPlz', e.target.value)}
                                placeholder="10115"
                            />
                        </div>
                        <div className="form-group">
                            <label>Stadt</label>
                            <input
                                type="text"
                                value={formData.billingCity}
                                onChange={(e) => handleInputChange('billingCity', e.target.value)}
                                placeholder="Berlin"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TischkontenDetailsComponent;
